import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaShoppingCart,
  FaTimes,
  FaWallet,
  FaCrown,
  FaMicrophone,
  FaArrowLeft,
  FaUser,
  FaSignOutAlt,
  FaChevronDown,
} from "react-icons/fa";
import { motion } from "framer-motion";
import ThemeToggleButton from "./ThemeToggleButton";
import { useTheme } from "../contexts/ThemeContext";
import { useThemeClasses } from "../hooks/useThemeClasses";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../auth/AuthContext";
import { getDisplayNameForUI } from "../utils/displayName";
import { productService, resolveProductMediaUrl } from "../services/apiService";
import { useLocationPreference } from "../contexts/LocationPreferenceContext";
import { isValidGeoCoord } from "../utils/geo";
import {
  buildCatalogSearchParams,
  persistExpressPreference,
  readExpressPreference,
  readSearchQueryFromUrl,
} from "../utils/searchFilters";
import { PRODUCT_PLACEHOLDER_SM, onImgErrorToPlaceholder } from "../utils/placeholderImage";
import {
  HEADER_MARKETPLACE_NAV_TABS,
  TOPBAR_UTILITY_LINKS,
  isStorefrontTabActive,
  isUtilityLinkActive,
} from "../config/storefrontNavTabs";
import { shouldShowBack, goBack } from "../utils/pageBack";
import { useHeaderScroll } from "../contexts/HeaderScrollContext";
const SPEECH_SUPPORTED =
  typeof window !== "undefined" &&
  !!(window.SpeechRecognition || window.webkitSpeechRecognition);

export const Header = () => {
  const RECENT_SEARCH_STORAGE_KEY = "sparepe_recent_searches";
  const MAX_RECENT_SEARCHES = 8;
  const SEARCH_DEBOUNCE_MS = 300;
  const MAX_SUGGESTIONS = 8;

  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const isHomePage = pathname === "/";
  const showBackButton = shouldShowBack(pathname);
  const { isDark } = useTheme();
  const { sc } = useThemeClasses();
  /** Header chrome follows global light/dark theme */
  const isDarkChrome = isDark;
  const { isAuthenticated, user, getDashboardPath } = useAuth();
  const [headerPN, setHeaderPN] = useState("");
  const { getTotalItems } = useCart();
  const [membershipType] = useState("Gold");

  const [isScrolled, setIsScrolled] = useState(false);
  const { isHeaderVisible } = useHeaderScroll();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [expressDelivery, setExpressDelivery] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const desktopHeaderRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const desktopSearchContainerRef = useRef(null);
  const mobileSearchContainerRef = useRef(null);
  const recognitionRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const {
    searchLocation,
    loading: isLocationLoading,
    error: locationError,
    radiusKm,
    showPrompt: isLocationPromptOpen,
    searchLocationLabel,
    reopenPrompt,
  } = useLocationPreference();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const q = readSearchQueryFromUrl(search);
    if (q) {
      setHeaderPN(q);
    }
    setExpressDelivery(readExpressPreference(search));
  }, [search]);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_SEARCH_STORAGE_KEY) || "[]");
      if (Array.isArray(stored)) {
        setRecentSearches(stored.filter((item) => typeof item === "string" && item.trim()));
      }
    } catch {
      setRecentSearches([]);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideDesktop = desktopSearchContainerRef.current?.contains(event.target);
      const clickedInsideMobile = mobileSearchContainerRef.current?.contains(event.target);
      if (!clickedInsideDesktop && !clickedInsideMobile) {
        setIsSearchOpen(false);
        setActiveSuggestionIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const syncHeaderOffset = () => {
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      const headerEl = isMobile ? mobileHeaderRef.current : desktopHeaderRef.current;
      if (!headerEl) return;
      document.documentElement.style.setProperty(
        "--sp-storefront-header-height",
        `${headerEl.offsetHeight}px`
      );
    };

    syncHeaderOffset();

    const observers = [];
    [desktopHeaderRef, mobileHeaderRef].forEach((ref) => {
      if (!ref.current) return;
      const observer = new ResizeObserver(syncHeaderOffset);
      observer.observe(ref.current);
      observers.push(observer);
    });

    window.addEventListener("resize", syncHeaderOffset);
    return () => {
      observers.forEach((observer) => observer.disconnect());
      window.removeEventListener("resize", syncHeaderOffset);
      document.documentElement.style.removeProperty("--sp-storefront-header-height");
    };
  }, []);

  const normalizeProduct = (product = {}) => {
    const imageCandidate =
      product.imageUrl ||
      product.image ||
      (Array.isArray(product.images) ? product.images[0] : "") ||
      (Array.isArray(product.imageUrls) ? product.imageUrls[0] : "");
    const categoryValue =
      product.categoryName ||
      product.category ||
      product.primaryCategory ||
      product.subCategory ||
      "Uncategorized";
    const keywords = Array.isArray(product.keywords)
      ? product.keywords
      : Array.isArray(product.tags)
        ? product.tags
        : typeof product.keyword === "string"
          ? product.keyword.split(",")
          : [];
    return {
      id: product._id || product.id || product.slug || product.sku || product.partNumber || product.name,
      name: String(product.name || product.title || product.partName || "Unnamed Product"),
      category: String(categoryValue),
      keywords: keywords.map((k) => String(k).trim()).filter(Boolean),
      price:
        Number(product.sellingPrice ?? product.price ?? product.finalPrice ?? product.mrp ?? 0) || 0,
      imageUrl: resolveProductMediaUrl(imageCandidate) || PRODUCT_PLACEHOLDER_SM,
      expressAvailable: !!product.expressAvailable,
      expectedDeliveryDate: product.expectedDeliveryDate || "",
      nearestShop: product.nearestShop || null,
      raw: product,
    };
  };

  const matchesNamePrefix = (product, query) => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return false;
    return String(product.name || "").trim().toLowerCase().startsWith(q);
  };

  const scoreProduct = (product, query) => {
    const q = query.toLowerCase().trim();
    if (!q || !matchesNamePrefix(product, q)) return Number.POSITIVE_INFINITY;
    const name = product.name.toLowerCase();
    return name === q ? 0 : 1;
  };

  const toCurrency = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    });

  const persistRecentSearch = (query) => {
    const clean = String(query || "").trim();
    if (!clean) return;
    setRecentSearches((prev) => {
      const next = [clean, ...prev.filter((item) => item.toLowerCase() !== clean.toLowerCase())].slice(
        0,
        MAX_RECENT_SEARCHES
      );
      try {
        localStorage.setItem(RECENT_SEARCH_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Ignore storage errors silently (private browsing or quota issues).
      }
      return next;
    });
  };

  const rankedSuggestions = useMemo(() => {
    const query = headerPN.trim();
    if (!query) return [];
    return suggestions
      .filter((item) => matchesNamePrefix(item, query))
      .map((item) => ({ ...item, score: scoreProduct(item, query) }))
      .sort((a, b) => {
        if (a.score !== b.score) return a.score - b.score;
        return a.name.localeCompare(b.name);
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [headerPN, suggestions]);

  const buildSearchFilters = (extra = {}) => {
    const filters = { ...extra };
    if (
      expressDelivery &&
      isValidGeoCoord(searchLocation?.lat, searchLocation?.lng)
    ) {
      filters.expressDelivery = true;
      filters.latitude = searchLocation.lat;
      filters.longitude = searchLocation.lng;
      filters.radiusKm = radiusKm;
    }
    return filters;
  };

  useEffect(() => {
    const query = headerPN.trim();
    if (!query) {
      setSuggestions([]);
      setIsSearchLoading(false);
      setSearchError("");
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearchLoading(true);
      setSearchError("");
      try {
        const response = await productService.getUserProducts(
          buildSearchFilters({ search: query, limit: 20, page: 1 })
        );
        const products = Array.isArray(response?.products)
          ? response.products
          : Array.isArray(response?.items)
            ? response.items
            : [];
        setSuggestions(products.map(normalizeProduct));
      } catch (error) {
        setSearchError(error?.message || "Could not load search suggestions");
      } finally {
        setIsSearchLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [headerPN, searchLocation, expressDelivery, radiusKm]);

  const handleExpressDeliveryChange = (enabled) => {
    setExpressDelivery(enabled);
    persistExpressPreference(enabled);
    if (pathname.startsWith("/catalog")) {
      const coords = isValidGeoCoord(searchLocation?.lat, searchLocation?.lng)
        ? searchLocation
        : null;
      const params = buildCatalogSearchParams({
        query: readSearchQueryFromUrl(search) || headerPN,
        express: enabled,
        coords,
        radiusKm,
        existingSearch: search,
      });
      navigate(`/catalog?${params.toString()}`, { replace: true });
    }
  };

  const handleSearch = (searchTerm = headerPN) => {
    const pn = String(searchTerm || "").trim();
    if (!pn) return;
    stopListening();
    persistRecentSearch(pn);
    persistExpressPreference(expressDelivery);
    setIsSearchOpen(false);
    setActiveSuggestionIndex(-1);
    const params = new URLSearchParams();
    params.set("mode", "part");
    params.set("q", pn);
    navigate(`/search?${params.toString()}`);
  };

  const handleSuggestionSelect = (item) => {
    const selectedName = item?.name || headerPN;
    setHeaderPN(selectedName);
    handleSearch(selectedName);
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    setIsListening(false);
  };

  const startListening = () => {
    if (!SPEECH_SUPPORTED) return;
    stopListening();
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.interimResults = true;
    recognition.continuous = false;
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
      }
      const next = transcript.trim();
      if (next) {
        setHeaderPN(next);
        setIsSearchOpen(true);
      }
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);

    recognition.start();
    setIsListening(true);
    setIsSearchOpen(true);
  };

  const handleVoiceSearch = () => {
    if (isListening) {
      stopListening();
      return;
    }
    startListening();
  };

  useEffect(() => () => stopListening(), []);

  const handleSearchFocus = () => {
    setIsSearchOpen(true);
  };

  const handleSearchKeyDown = (e) => {
    if (!isSearchOpen) {
      if (e.key === "ArrowDown" && rankedSuggestions.length > 0) {
        setIsSearchOpen(true);
        setActiveSuggestionIndex(0);
      }
      if (e.key === "Enter") {
        e.preventDefault();
        handleSearch();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestionIndex((prev) =>
        rankedSuggestions.length === 0 ? -1 : (prev + 1) % rankedSuggestions.length
      );
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestionIndex((prev) =>
        rankedSuggestions.length === 0
          ? -1
          : prev <= 0
            ? rankedSuggestions.length - 1
            : prev - 1
      );
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (activeSuggestionIndex >= 0 && rankedSuggestions[activeSuggestionIndex]) {
        handleSuggestionSelect(rankedSuggestions[activeSuggestionIndex]);
      } else {
        handleSearch();
      }
      return;
    }
    if (e.key === "Escape") {
      setIsSearchOpen(false);
      setActiveSuggestionIndex(-1);
    }
  };

  const renderSearchDropdown = () => {
    const showContextShortcuts = !headerPN.trim();
    return (
      <div className="header-search-dropdown absolute left-0 right-0 top-full mt-1 z-[70] rounded-xl border border-slate-200/80 bg-white shadow-[0_18px_42px_rgba(15,23,42,0.22)] max-h-[70vh] overflow-y-auto">
        {showContextShortcuts ? (
          <div className="p-3 space-y-3">
            {isListening && (
              <p className="text-xs font-medium text-blue-600">Listening… speak now</p>
            )}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Recent searches</p>
              <div className="flex flex-wrap gap-2">
                {recentSearches.length > 0 ? (
                  recentSearches.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => handleSearch(item)}
                      className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-xs text-slate-700 transition-colors"
                    >
                      {item}
                    </button>
                  ))
                ) : (
                  <p className="text-xs text-slate-500">No recent searches yet.</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="py-2">
            <div className="px-4 py-2 border-b border-slate-100">
              <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={expressDelivery}
                  onChange={(e) => handleExpressDeliveryChange(e.target.checked)}
                  className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                />
                <span className="font-semibold">Express delivery (within {radiusKm} km)</span>
              </label>
            </div>
            {expressDelivery && isLocationLoading && (
              <p className="px-4 py-2 text-xs text-slate-500">Detecting your location…</p>
            )}
            {expressDelivery && !isLocationLoading && isLocationPromptOpen && (
              <p className="px-4 py-2 text-xs text-amber-700">
                Choose your location for express delivery.{" "}
                <button type="button" onClick={reopenPrompt} className="font-semibold underline">
                  Choose now
                </button>
              </p>
            )}
            {expressDelivery && !isLocationLoading && locationError && (
              <p className="px-4 py-2 text-xs text-amber-700">{locationError}</p>
            )}
            {expressDelivery && !isLocationLoading && !isLocationPromptOpen && isValidGeoCoord(searchLocation?.lat, searchLocation?.lng) && (
              <div className="px-4 py-2 text-[11px] text-slate-500 border-b border-slate-100 flex items-center justify-between gap-2">
                <span>
                  Express from shops within {radiusKm} km of {searchLocationLabel || "your location"}
                </span>
                <button
                  type="button"
                  onClick={reopenPrompt}
                  className="text-amber-700 font-semibold hover:underline shrink-0"
                >
                  Change
                </button>
              </div>
            )}
            {isSearchLoading && <p className="px-4 py-2 text-xs text-slate-500">Searching products…</p>}
            {!isSearchLoading && searchError && <p className="px-4 py-2 text-xs text-rose-500">{searchError}</p>}
            {!isSearchLoading && !searchError && rankedSuggestions.length === 0 && (
              <p className="px-4 py-2 text-xs text-slate-500">No matching products found.</p>
            )}
            {rankedSuggestions.map((item, index) => (
              <button
                key={`${item.id}-${index}`}
                type="button"
                onMouseEnter={() => setActiveSuggestionIndex(index)}
                onClick={() => handleSuggestionSelect(item)}
                className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${
                  activeSuggestionIndex === index ? "bg-amber-50" : "hover:bg-slate-50"
                }`}
              >
                <img
                  loading="lazy"
                  src={item.imageUrl}
                  alt={item.name}
                  className="h-12 w-12 rounded-md object-cover border border-slate-200 shrink-0 bg-slate-50"
                  onError={(e) => {
                    onImgErrorToPlaceholder(e, 56, 56, 'Part');
                  }}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">{item.name}</p>
                  <p className="text-xs text-slate-500 truncate">{item.category}</p>
                  {expressDelivery && item.raw?.expressAvailable && (
                    <p className="text-[10px] font-semibold text-emerald-600 mt-0.5 truncate">
                      Express · by{" "}
                      {item.raw.expectedDeliveryDateTime || item.raw.expectedDeliveryDate
                        ? new Date(
                            item.raw.expectedDeliveryDateTime || item.raw.expectedDeliveryDate
                          ).toLocaleString("en-IN", {
                            day: "numeric",
                            month: "short",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })
                        : "—"}
                    </p>
                  )}
                  {expressDelivery && item.raw && !item.raw.expressAvailable && (
                    <p className="text-[10px] text-amber-700 mt-0.5 truncate">
                      {item.raw.nearestShop?.name
                        ? `Nearest shop: ${item.raw.nearestShop.name} · `
                        : ""}
                      Delivers by{" "}
                      {item.raw.expectedDeliveryDateTime || item.raw.expectedDeliveryDate
                        ? new Date(
                            item.raw.expectedDeliveryDateTime || item.raw.expectedDeliveryDate
                          ).toLocaleString("en-IN", {
                            day: "numeric",
                            month: "short",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })
                        : "—"}
                    </p>
                  )}
                </div>
                <p className="text-sm font-bold text-slate-900 whitespace-nowrap">{toCurrency(item.price)}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const goToCart = () => navigate("/cart");
  const goToHomePage = () => navigate("/");

  const displayName = getDisplayNameForUI(user);
  const isDemoGarageAccount =
    isAuthenticated &&
    (["demo garage", "demo store"].includes(
      String(displayName || user?.name || user?.garageDetails?.garageName || "")
        .trim()
        .toLowerCase()
    ) ||
      String(user?.garageDetails?.garageName || "").trim().toLowerCase() ===
        "demo garage");

  const accountLabel =
    !isAuthenticated || isDemoGarageAccount
      ? "Sign in"
      : displayName || "Account";

  const topbarUtilityLinks = TOPBAR_UTILITY_LINKS;

  const handleAccountClick = () => {
    if (!isAuthenticated || isDemoGarageAccount) {
      navigate("/login");
      return;
    }
    const role = (user?.role || "").toString().toLowerCase();
    if (role === "customer" || !role) {
      navigate("/account");
      return;
    }
    navigate(getDashboardPath(user?.role) || "/customer/dashboard");
  };

  const tabActive = (tab) => isStorefrontTabActive(tab, pathname, search);

  return (
    <>
      <motion.header
        ref={desktopHeaderRef}
        className={`sparepe-header hidden md:block w-full fixed top-0 left-0 right-0 z-50 ${sc(
          "headerChrome"
        )} ${
          isScrolled
            ? isDarkChrome
              ? "shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
              : "shadow-xl bg-white/95"
            : isDarkChrome
              ? "shadow-[0_4px_24px_rgba(0,0,0,0.18)]"
              : "shadow-sm"
        }`}
        initial={{ y: 0 }}
        animate={{
          y: isHeaderVisible ? 0 : -120,
          opacity: isHeaderVisible ? 1 : 0,
        }}
      >
        {/* Topbar — HTML mockup */}
        {isDarkChrome ? (
          <div className="sparepe-header__utility sparepe-header__utility-inner flex items-center justify-between gap-4 py-1.5 text-[11px]">
            <span className="sparepe-header__promo topbar-promo shrink-0">
              🚚 Free delivery above ₹1,999 · GST invoice on all orders
            </span>
            <div className="hidden sm:flex items-center gap-5 shrink-0 ml-auto">
              {topbarUtilityLinks.map((link) => (
                <Link
                  key={link.id || link.href}
                  to={link.href}
                  className={`sparepe-header__utility-link whitespace-nowrap ${
                    isUtilityLinkActive(link, pathname) ? "text-sparepe-accent-dk" : ""
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="px-6 py-1.5 flex items-center justify-between text-[11px] text-gray-500 border-b border-gray-100">
            <span className="text-amber-700 font-medium">
              Free delivery above ₹1,999 · GST invoice on all orders
            </span>
            <div className="hidden sm:flex items-center gap-5 shrink-0 ml-auto">
              {topbarUtilityLinks.map((link) => (
                <Link
                  key={link.id || link.href}
                  to={link.href}
                  className="hover:text-gray-900 transition-colors whitespace-nowrap"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Main header row — logo · nav · search · actions */}
        <div
          className={`sparepe-header__main-inner w-full ${
            isDarkChrome ? "sparepe-header__main py-2.5" : "py-3"
          }`}
        >
          <div className="sparepe-header__main-row grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 lg:gap-3">
            <div className="flex min-w-0 items-center gap-2 lg:gap-3 shrink-0">
            {showBackButton && (
              <button
                type="button"
                onClick={() => goBack(navigate)}
                className={`sparepe-header__back-btn shrink-0 ${
                  isDarkChrome ? "" : "text-gray-700 hover:text-gray-900"
                }`}
                aria-label="Go back"
              >
                <FaArrowLeft className="text-sm" />
                <span className="hidden lg:inline">Back</span>
              </button>
            )}
            <motion.button
              type="button"
              onClick={goToHomePage}
              className="shrink-0 cursor-pointer bg-transparent border-0 p-0"
              aria-label="Sparepe home"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <img
                loading="lazy"
                src="/sparepe.jpeg"
                alt="Sparepe"
                className="sparepe-header__logo-img h-14 w-auto sm:h-16 md:h-20"
              />
            </motion.button>

            <nav className="sparepe-header__nav shrink-0" aria-label="Marketplace">
              {HEADER_MARKETPLACE_NAV_TABS.map((tab) => {
                const active = tabActive(tab);
                return (
                  <Link
                    key={tab.id}
                    to={tab.href}
                    className={`sparepe-header__nav-tab ${
                      active ? "sparepe-header__nav-tab--active" : ""
                    }`}
                  >
                    {tab.label}
                  </Link>
                );
              })}
            </nav>
            </div>

            <div
              ref={desktopSearchContainerRef}
              className="sparepe-header__search-wrap relative min-w-0 px-0 lg:px-2"
            >
              <div className="sparepe-header__search w-full">
                <input
                  type="text"
                  placeholder={isHomePage ? "Search parts for your car..." : "Search parts, OEM numbers, brands…"}
                  className="sparepe-header__search-input"
                  value={headerPN}
                  onChange={(e) => {
                    setHeaderPN(e.target.value);
                    setIsSearchOpen(true);
                  }}
                  onFocus={handleSearchFocus}
                  onKeyDown={handleSearchKeyDown}
                />
                {headerPN && (
                  <button
                    type="button"
                    onClick={() => setHeaderPN("")}
                    className="sparepe-header__search-action lg:hidden"
                    aria-label="Clear search"
                  >
                    <FaTimes className="text-xs" />
                  </button>
                )}
                {isHomePage && SPEECH_SUPPORTED && (
                  <button
                    type="button"
                    onClick={handleVoiceSearch}
                    className={`sparepe-header__search-action ${
                      isListening ? "sparepe-header__search-action--active" : ""
                    }`}
                    aria-label={isListening ? "Stop voice search" : "Voice search"}
                  >
                    <FaMicrophone className="text-sm" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSearch}
                  className="sparepe-header__search-btn"
                >
                  Search
                </button>
              </div>
              {isSearchOpen && renderSearchDropdown()}
            </div>

            <div className="sparepe-header__actions flex items-center justify-end gap-0.5 sm:gap-1 shrink-0">
              <ThemeToggleButton variant="header" isDarkChrome={isDarkChrome} />

              <motion.button
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate("/membership")}
                className={`hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-sparepe text-xs font-semibold transition-all ${
                  isDarkChrome
                    ? "sparepe-header__compact-btn sparepe-header__compact-btn--gold"
                    : "bg-gradient-to-r from-amber-300 to-yellow-400 text-amber-950 border border-amber-400"
                }`}
                aria-label="Membership"
              >
                <FaCrown className="text-sm shrink-0" />
                <span>{membershipType}</span>
              </motion.button>

              <motion.button
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate("/wallet")}
                className={`hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-sparepe text-xs font-semibold transition-all ${
                  isDarkChrome
                    ? "sparepe-header__compact-btn"
                    : "border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"
                }`}
                aria-label="Wallet"
              >
                <FaWallet className="text-sm shrink-0" />
                <span>Wallet</span>
              </motion.button>

              <motion.button
                type="button"
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.95 }}
                onClick={goToCart}
                className={`sparepe-header__icon-btn sparepe-header__iBtn relative ${
                  isDarkChrome ? "" : "p-2 hover:bg-gray-100 rounded-lg text-gray-600"
                }`}
                aria-label="Cart"
              >
                <FaShoppingCart className="text-base" />
                {getTotalItems() > 0 && (
                  <span className="sparepe-header__badge cart-count">
                    {getTotalItems()}
                  </span>
                )}
              </motion.button>

              <motion.button
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleAccountClick}
                className={
                  isDarkChrome
                    ? "sparepe-header__signin login-btn ml-1"
                    : "ml-1 px-4 py-2 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700"
                }
              >
                {accountLabel.length > 14
                  ? `${accountLabel.slice(0, 12)}…`
                  : accountLabel}
              </motion.button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile header — HTML mockup (screens < md) */}
      <motion.header
        ref={mobileHeaderRef}
        className={`sparepe-header sparepe-header--mobile md:hidden w-full fixed top-0 left-0 right-0 z-50 ${sc(
          "headerChrome"
        )}`}
        initial={{ y: 0 }}
        animate={{
          y: isHeaderVisible ? 0 : -140,
          opacity: isHeaderVisible ? 1 : 0,
        }}
      >
        <div
          className={`sparepe-header__utility sparepe-header__utility-inner flex items-center justify-between gap-2 py-1 text-[10px] ${
            isDarkChrome ? "" : "text-gray-500 border-b border-gray-100"
          }`}
        >
          <span
            className={
              isDarkChrome
                ? "sparepe-header__promo topbar-promo truncate"
                : "text-amber-700 font-medium truncate"
            }
          >
            🚚 Free delivery above ₹1,999
          </span>
          <Link
            to="/garages"
            className={
              isDarkChrome
                ? "sparepe-header__utility-link shrink-0"
                : "shrink-0 hover:text-gray-900"
            }
          >
            Garages
          </Link>
        </div>

        <div className="sparepe-header__main-inner px-3 py-2 space-y-2">
          {showBackButton && (
            <button
              type="button"
              onClick={() => goBack(navigate)}
              className={`sparepe-header__back-btn w-fit ${
                isDarkChrome ? "" : "text-gray-700"
              }`}
              aria-label="Go back"
            >
              <FaArrowLeft className="text-xs" />
              <span>Back</span>
            </button>
          )}
          <div className="flex items-center justify-between gap-2 min-w-0">
            <button
              type="button"
              onClick={goToHomePage}
              className="shrink-0 bg-transparent border-0 p-0"
              aria-label="Sparepe home"
            >
              <img
                loading="lazy"
                src="/sparepe.jpeg"
                alt="Sparepe"
                className="sparepe-header__logo-img h-11 w-auto"
              />
            </button>
            <div className="flex items-center gap-0.5 shrink-0">
              <ThemeToggleButton variant="header" isDarkChrome={isDarkChrome} />

              <button
                type="button"
                onClick={goToCart}
                className={`sparepe-header__icon-btn sparepe-header__iBtn relative ${
                  isDarkChrome ? "" : "p-2 text-gray-600"
                }`}
                aria-label="Cart"
              >
                <FaShoppingCart className="text-sm" />
                {getTotalItems() > 0 && (
                  <span className="sparepe-header__badge cart-count">
                    {getTotalItems()}
                  </span>
                )}
              </button>
              <motion.button
                type="button"
                whileTap={{ scale: 0.97 }}
                onClick={handleAccountClick}
                className={
                  isDarkChrome
                    ? "sparepe-header__signin login-btn shrink-0 text-xs px-2.5 py-1.5"
                    : "shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-blue-600"
                }
              >
                {accountLabel.length > 10
                  ? `${accountLabel.slice(0, 8)}…`
                  : accountLabel}
              </motion.button>
            </div>
          </div>

          <div ref={mobileSearchContainerRef} className="sparepe-header__mobile-search-row relative w-full">
            <div className="sparepe-header__search w-full">
              <input
                type="text"
                placeholder={isHomePage ? "Search parts for your car..." : "Part name, OEM, vehicle…"}
                className="sparepe-header__search-input"
                value={headerPN}
                onChange={(e) => {
                  setHeaderPN(e.target.value);
                  setIsSearchOpen(true);
                }}
                onFocus={handleSearchFocus}
                onKeyDown={handleSearchKeyDown}
              />
              {headerPN && (
                <button
                  type="button"
                  onClick={() => setHeaderPN("")}
                  className="sparepe-header__search-action shrink-0"
                  aria-label="Clear search"
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
              {isHomePage && SPEECH_SUPPORTED && (
                <button
                  type="button"
                  onClick={handleVoiceSearch}
                  className={`sparepe-header__search-action shrink-0 ${
                    isListening ? "sparepe-header__search-action--active" : ""
                  }`}
                  aria-label={isListening ? "Stop voice search" : "Voice search"}
                >
                  <FaMicrophone className="text-sm" />
                </button>
              )}
              <button
                type="button"
                onClick={handleSearch}
                className="sparepe-header__search-btn"
              >
                Search
              </button>
            </div>
            {isSearchOpen && renderSearchDropdown()}
          </div>

          <div className="sparepe-header__mobile-tabs">
            {HEADER_MARKETPLACE_NAV_TABS.map((tab) => {
              const active = tabActive(tab);
              return (
                <Link
                  key={tab.id}
                  to={tab.href}
                  className={`sparepe-header__mobile-tab ${
                    active ? "sparepe-header__mobile-tab--active" : ""
                  }`}
                >
                  {tab.shortLabel || tab.label}
                </Link>
              );
            })}
          </div>
        </div>
      </motion.header>
    </>
  );
};

export default Header;
